import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { partnerArApi, receivableBadge, type ReceivableFilter } from "@/services/partnerAr";
import { formatCents } from "@/services/finance";
import { Pill } from "@/shared/ui/PageHeader";

/**
 * 应收明细（一单一笔）— 样稿 credit-mockup-v1 ②。
 * 同行详情「订单 & 对账」里一块（partnerId 有值）；结算中心是跨同行的同一张表（partnerId 为空 → 多一列同行）。
 * 数据 = 统一的 receivables 表（财务「应收」页同表同口径）。
 */
const CHIPS: { key: ReceivableFilter; label: string }[] = [
  { key: "open", label: "全部未收" },
  { key: "overdue", label: "已逾期" },
  { key: "paid", label: "已收" },
  { key: "void", label: "已作废" },
];

const md = (iso: string | null) => (iso ? iso.slice(5) : "—");

export function ReceivablesCard({ partnerId, onRegister }: { partnerId?: string; onRegister?: () => void }) {
  const [filter, setFilter] = useState<ReceivableFilter>("open");
  const q = useQuery({
    queryKey: ["b2b", "receivables", partnerId ?? "all", filter],
    queryFn: () => (partnerId ? partnerArApi.receivables(partnerId, filter) : partnerArApi.allReceivables(filter)),
  });
  const d = q.data;
  const cols = partnerId ? 8 : 9;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="receivables-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-[15px] font-semibold text-slate-800">应收明细</h3>
        <span className="text-[12px] text-slate-400">一单一笔 · 确认订单即生成</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {CHIPS.map((c) => (
            <button key={c.key} type="button" onClick={() => setFilter(c.key)}
              className={`rounded-full border px-3 py-0.5 text-[12.5px] ${filter === c.key ? "border-blue-600 bg-blue-50 text-blue-600" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>
              {c.label}{d ? ` · ${d.counts[c.key]}` : ""}
            </button>
          ))}
          {onRegister && <button type="button" onClick={onRegister} data-testid="register-payment-btn" className="h-8 rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white">登记收款</button>}
        </div>
      </div>

      {q.isLoading ? <div className="py-6 text-center text-[12px] text-slate-400">加载中…</div>
        : q.isError ? <div className="py-6 text-center text-[12px] text-slate-500">应收加载失败 <button type="button" onClick={() => q.refetch()} className="ml-2 text-blue-600 hover:underline">重试</button></div>
        : (
          <div className="overflow-x-auto rounded-[10px] border border-slate-200">
            <table className="w-full min-w-[760px] text-[13px] tabular-nums">
              <thead className="bg-slate-50 text-[12px] font-medium text-slate-400">
                <tr>
                  <th className="px-2.5 py-2 text-left">订单</th>
                  {!partnerId && <th className="px-2.5 py-2 text-left">同行</th>}
                  <th className="px-2.5 py-2 text-left">产品</th>
                  <th className="px-2.5 py-2 text-left">出发</th>
                  <th className="px-2.5 py-2 text-right">应收</th>
                  <th className="px-2.5 py-2 text-right">已收</th>
                  <th className="px-2.5 py-2 text-right">未收</th>
                  <th className="px-2.5 py-2 text-left">到期</th>
                  <th className="px-2.5 py-2 text-left">状态</th>
                </tr>
              </thead>
              <tbody>
                {(d?.rows ?? []).map((r) => {
                  const b = receivableBadge(r);
                  return (
                    <tr key={r.id} className="border-t border-slate-100" data-testid="receivable-row">
                      <td className="px-2.5 py-2">{r.order_id && r.order_no ? <Link to={`/admin/orders/${r.order_id}`} className="text-blue-600 hover:underline">{r.order_no}</Link> : (r.order_no ?? r.receivable_number)}</td>
                      {!partnerId && <td className="px-2.5 py-2"><Link to={`/admin/channel-partners/${r.partner_id}`} className="text-slate-800 hover:underline">{r.partner_name ?? "—"}</Link></td>}
                      <td className="px-2.5 py-2">{r.product_name ?? "—"}{r.pax_count ? ` · ${r.pax_count} 人` : ""}</td>
                      <td className="px-2.5 py-2">{md(r.travel_date)}</td>
                      <td className="px-2.5 py-2 text-right">{formatCents(r.amount_cents)}</td>
                      <td className="px-2.5 py-2 text-right">{formatCents(r.paid_cents)}</td>
                      <td className="px-2.5 py-2 text-right font-medium">{formatCents(r.outstanding_cents)}</td>
                      <td className="px-2.5 py-2">{md(r.due_date)}</td>
                      <td className="px-2.5 py-2"><Pill tone={b.tone}>{b.label}</Pill></td>
                    </tr>
                  );
                })}
                {(d?.rows ?? []).length === 0 && <tr><td colSpan={cols} className="px-4 py-6 text-center text-slate-400">{filter === "void" ? "没有已作废的应收" : "没有应收"}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      {d && filter !== "void" && (
        <div className="mt-2 text-[12px] text-slate-500">未收合计 <b className="text-slate-800">{formatCents(d.open_cents)}</b>{d.overdue_cents > 0 && <> · 其中已逾期 <b className="text-rose-600">{formatCents(d.overdue_cents)}</b></>}</div>
      )}
      <div className="mt-3 rounded-[10px] border border-dashed border-slate-300 px-3 py-2 text-[12.5px] text-slate-600">
        生成规则：OP 确认（或平台自动确认）月结 / 单结同行的订单 → 生成一笔，金额 = 订单上的同行结算价；改单改金额 → 应收跟着改（已收超过新金额 → 多出部分记预收余额）；订单取消 → 未收部分作废、已收部分记预收余额。预付同行不生成。
      </div>
    </section>
  );
}
